import axios from 'axios';
import { getToken } from './auth';

// Create an axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Request API functions
export const requestApi = {
  // Get all requests with optional filters
  getRequests: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.category) {
      params.append('category', filters.category);
    }

    if (filters.status) {
      params.append('status', filters.status);
    }

    if (filters.page) {
      params.append('page', filters.page);
    }

    if (filters.pageSize) {
      params.append('pageSize', filters.pageSize);
    }

    if (filters.searchString) {
      params.append('searchString', filters.searchString);
    }

    if (filters.fromDate) {
      params.append('fromDate', filters.fromDate);
    }

    if (filters.toDate) {
      params.append('toDate', filters.toDate);
    }

    // Add sorting parameters
    if (filters.orderBy) {
      // Use - prefix for descending, no prefix for ascending
      const orderParam =
        filters.orderDirection === 'asc'
          ? filters.orderBy // No prefix for ascending
          : `-${filters.orderBy}`; // - prefix for descending

      // Manually append to the URL instead of using params.append
      return api.get(
        `/requests?${params.toString()}${
          params.toString() ? '&' : ''
        }orderBy=${orderParam}`,
      );
    }

    return api.get(`/requests?${params.toString()}`);
  },

  // Get a single request by ID
  getRequest: async (id) => {
    return api.get(`/requests/${id}`);
  },

  // Update the status of a request
  updateStatus: async (id, status) => {
    return api.patch(`/requests/${id}/status`, { status });
  },

  // Export requests to CSV
  exportRequests: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.category) {
      params.append('category', filters.category);
    }

    if (filters.status) {
      params.append('status', filters.status);
    }

    if (filters.searchString) {
      params.append('searchString', filters.searchString);
    }

    if (filters.fromDate) {
      params.append('fromDate', filters.fromDate);
    }

    if (filters.toDate) {
      params.append('toDate', filters.toDate);
    }

    // Add sorting parameters
    if (filters.orderBy) {
      const orderParam =
        filters.orderDirection === 'asc'
          ? filters.orderBy
          : `-${filters.orderBy}`;
      params.append('orderBy', orderParam);
    }

    return api.get(`/requests/export?${params.toString()}`, {
      responseType: 'blob',
    });
  },

  // Decrypt request data
  decryptRequest: async (id) => {
    return api.get(`/requests/${id}/decrypt`);
  },

  // Get request response data
  getRequestResponse: async (id) => {
    return api.get(`/requests/${id}/response`);
  },
};

export const botServerApi = {
  getServers: async () => {
    return api.get('/bot-2/servers');
  },

  // Which machine holds the warm POS session for each account. The bots keep a
  // Chromium profile per (link, username), so a task sent to the box that
  // already has it skips the login entirely.
  getAffinity: async () => {
    return api.get('/bot-2/affinity');
  },

  // Pin an account to a server, or pass pinned: false to let it follow
  // whichever server ran it last.
  setAffinity: async ({ uniqueKey, category, botServerId, pinned }) => {
    return api.put('/bot-2/affinity', { uniqueKey, category, botServerId, pinned });
  },

  // Queue a task that signs this cashier out and frees the warm profile. It
  // goes through the normal queue, so it lands on the machine actually
  // holding the session and cannot run while that account is mid-transaction.
  logoutSession: async ({ uniqueKey, category }) => {
    return api.post('/bot-2/affinity/logout', { uniqueKey, category });
  },
};

// Migration API functions
export const migrationApi = {
  // Get available categories
  getCategories: async () => {
    const token = getToken();
    return axios.get('https://autobot-api-dev.diaflow.io/api/v1/categories', {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  },

  // Create a migration request (old external API for listing accounts)
  createMigrationRequest: async (requestData) => {
    const token = getToken();
    return axios.post(
      'https://autobot-api-dev.diaflow.io/api/v1/requests',
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );
  },

  // Get migration request status (old external API)
  getMigrationRequest: async (id) => {
    const token = getToken();
    return axios.get(
      `https://autobot-api-dev.diaflow.io/api/v1/requests/${id}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );
  },

  // New Migration Request API functions
  // Create a new migration request
  createNewMigrationRequest: async (requestData) => {
    return api.post('/migration/requests', requestData);
  },

  // Get all migration requests
  getAllMigrationRequests: async (page = 1, limit = 10, includeStats = false) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (includeStats) {
      params.append('includeStats', 'true');
    }
    return api.get(`/migration/requests?${params.toString()}`);
  },

  // Get a specific migration request
  getNewMigrationRequest: async (id) => {
    return api.get(`/migration/requests/${id}`);
  },

  // Update migration request
  updateMigrationRequest: async (id, updateData) => {
    return api.patch(`/migration/requests/${id}`, updateData);
  },

  // Delete migration request
  deleteMigrationRequest: async (id) => {
    return api.delete(`/migration/requests/${id}`);
  },

  // Migration Request Items API functions
  // Create migration request item
  createMigrationRequestItem: async (itemData) => {
    return api.post('/migration/items', itemData);
  },

  // Create migration request items in bulk (max 50 items)
  createMigrationRequestItemsBulk: async (items) => {
    return api.post('/migration/items/bulk', { items });
  },

  // Get all migration request items
  getAllMigrationRequestItems: async (
    migrationRequestId,
    page = 1,
    limit = 10,
    status = null,
  ) => {
    const params = new URLSearchParams();
    if (migrationRequestId) {
      params.append('migrationRequestId', migrationRequestId);
    }
    params.append('page', page);
    params.append('limit', limit);
    if (status && status !== 'ALL') {
      params.append('status', status);
    }
    return api.get(`/migration/items?${params.toString()}`);
  },

  // Get a specific migration request item
  getMigrationRequestItem: async (id) => {
    return api.get(`/migration/items/${id}`);
  },

  // Update migration request item
  updateMigrationRequestItem: async (id, updateData) => {
    return api.patch(`/migration/items/${id}`, updateData);
  },

  // Delete migration request item
  deleteMigrationRequestItem: async (id) => {
    return api.delete(`/migration/items/${id}`);
  },

  // Start migration process
  startMigrationProcess: async (migrationRequestId, migrationRequestData) => {
    const token = getToken();
    // Ensure dataDes exists before accessing its properties
    const dataDes = migrationRequestData.dataDes || {};
    return axios.post(
      'https://autobot-api-dev.diaflow.io/api/v1/requests',
      {
        webhookUrl: null,
        sessionId: migrationRequestData.sessionId,
        category: migrationRequestData.categoryDes,
        data: {
          action: 'migration',
          link: migrationRequestData.linkDes,
          username: dataDes.username || '',
          password: dataDes.password || '',
          migration_id: migrationRequestId,
        },
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );
  },

  // Export to XLSX
  exportMigrationRequest: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.category) {
      params.append('category', filters.category);
    }

    if (filters.status) {
      params.append('status', filters.status);
    }

    if (filters.searchString) {
      params.append('searchString', filters.searchString);
    }

    if (filters.fromDate) {
      params.append('fromDate', filters.fromDate);
    }

    if (filters.toDate) {
      params.append('toDate', filters.toDate);
    }

    // Add sorting parameters
    if (filters.orderBy) {
      const orderParam =
        filters.orderDirection === 'asc'
          ? filters.orderBy
          : `-${filters.orderBy}`;
      params.append('orderBy', orderParam);
    }

    if (filters.id) {
      params.append('migrationRequestId', filters.id);
    }

    return api.get(`/migration/export?${params.toString()}`, {
      responseType: 'blob',
    });
  },

  // Check for duplicate customers
  checkDuplication: async (customers, targetLink) => {
    return api.post('/migration/check-duplication', {
      customers,
      targetLink,
    });
  },
};

export default api;
